/**
 * @fileoverview Job Agent tab — universal AI chat scoped to one job
 *
 * URL: /dashboard/jobs/:jobId/agent
 *
 * Lets the practitioner search across this job's data agents,
 * contacts, and documents, and chat with the Morgan universal agent.
 *
 * Available to any authenticated user with access to the job; access
 * to the underlying data is enforced by the Convex backend.
 */

import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Loader2 } from "lucide-react";
import { JobAgentView } from "@/components/agents/universal";

export const Route = createFileRoute(
  "/_app/_auth/dashboard/_layout/jobs/$jobId/agent",
)({
  validateSearch: (search: Record<string, unknown>) => ({
    threadId: typeof search.threadId === "string" ? search.threadId : undefined,
  }),
  component: JobAgentPage,
});

function JobAgentPage() {
  const { jobId } = Route.useParams();
  const { threadId } = Route.useSearch();
  const { data: user, isLoading } = useQuery(
    convexQuery(api.users.queries.getCurrentUser, {}),
  );

  if (isLoading || !user) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <JobAgentView jobId={jobId as Id<"jobs">} initialThreadId={threadId} />
  );
}
